'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CaseCard, toCaseCardImage } from '@/components/parts/case-card'
import { FilterPanel, type FilterGroup } from '@/components/parts/filter-panel'
import { Pager } from '@/components/parts/pager'
import type { CommonNewsCategory } from '@/types/microcms/common-news-category'
import type { News } from '@/types/microcms/news'
import {
  NEWS_PAGE_SIZE,
  getNewsCategoryNames,
  getNewsDate,
  getNewsHref,
  getNewsListHref,
  getNewsTags,
  isFraimNews
} from '@/utils/news'

type NewsListSectionProps = {
  /** 全件（microCMS の並び順のまま）。表示するページはここで切り出す */
  news: News[]
  /** 絞り込みに出すカテゴリー（LAWGUE サイトのもの） */
  categories: CommonNewsCategory[]
  /** 表示中のページ。/news は 1、/news/page/[page] はその番号 */
  currentPage: number
}

type Applied = { categories: string[]; keyword: string }

const EMPTY: Applied = { categories: [], keyword: '' }

const hasConditions = (applied: Applied) =>
  applied.categories.length > 0 || applied.keyword !== ''

/** 絞り込み条件を URL に残す（旧実装と同じ categories / search / page） */
const writeUrl = (applied: Applied, page: number) => {
  const url = new URL(window.location.href)
  for (const key of ['categories', 'search', 'page']) {
    url.searchParams.delete(key)
  }
  if (applied.categories.length > 0) {
    url.searchParams.set('categories', applied.categories.join(','))
  }
  if (applied.keyword) url.searchParams.set('search', applied.keyword)
  if (page > 1) url.searchParams.set('page', String(page))

  // 絞り込み結果は /news で復元するので、/news/page/[page] で絞り込んだときもパスを /news/ に寄せる
  const pathname = hasConditions(applied) ? getNewsListHref(1) : url.pathname
  window.history.replaceState({}, '', pathname + url.search)
}

/**
 * ニュース一覧の本体。絞り込み + カード 3 列 + ページャ。
 * 絞り込みは「選択した条件で絞り込む」を押したときに反映し、結果の中ではクライアント側でページを送る。
 * 条件が無いときはページごとのルート（/news/page/[page]）へリンクで送る。
 */
export function NewsListSection({
  news,
  categories,
  currentPage
}: NewsListSectionProps) {
  const [selected, setSelected] = useState<string[]>([])
  const [keyword, setKeyword] = useState('')
  const [applied, setApplied] = useState<Applied>(EMPTY)
  const [filteredPage, setFilteredPage] = useState(1)
  const [isInitialized, setIsInitialized] = useState(false)
  const sectionRef = useRef<HTMLElement>(null)

  // URL パラメータから絞り込みを復元する
  useEffect(() => {
    if (isInitialized) return

    const params = new URLSearchParams(window.location.search)
    const initial: Applied = {
      categories: params.get('categories')?.split(',').filter(Boolean) ?? [],
      keyword: params.get('search')?.trim() ?? ''
    }

    setSelected(initial.categories)
    setKeyword(initial.keyword)
    setApplied(initial)
    setFilteredPage(
      Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1)
    )
    setIsInitialized(true)
  }, [isInitialized])

  const groups: FilterGroup[] = useMemo(
    () => [
      {
        key: 'categories',
        label: 'カテゴリー',
        items: categories.map((category) => category.name)
      }
    ],
    [categories]
  )

  const handleToggle = useCallback((_groupKey: string, item: string) => {
    setSelected((prev) =>
      prev.includes(item)
        ? prev.filter((value) => value !== item)
        : [...prev, item]
    )
  }, [])

  const handleSubmit = useCallback(() => {
    const next: Applied = { categories: selected, keyword: keyword.trim() }
    setApplied(next)
    setFilteredPage(1)
    writeUrl(next, 1)
  }, [selected, keyword])

  const handlePageChange = useCallback(
    (page: number) => {
      setFilteredPage(page)
      writeUrl(applied, page)
      sectionRef.current?.scrollIntoView({ behavior: 'smooth' })
    },
    [applied]
  )

  const filteredNews = useMemo(() => {
    if (!hasConditions(applied)) return news

    const term = applied.keyword.toLowerCase()
    return news.filter((item) => {
      if (
        applied.categories.length > 0 &&
        !getNewsCategoryNames(item).some((name) =>
          applied.categories.includes(name)
        )
      ) {
        return false
      }
      if (!term) return true

      return [item.title, item.description, item.contents].some((field) =>
        field?.toLowerCase().includes(term)
      )
    })
  }, [news, applied])

  const isFiltered = hasConditions(applied)
  const totalPages = Math.max(
    1,
    Math.ceil(filteredNews.length / NEWS_PAGE_SIZE)
  )
  const page = isFiltered ? Math.min(filteredPage, totalPages) : currentPage
  const visibleNews = filteredNews.slice(
    (page - 1) * NEWS_PAGE_SIZE,
    page * NEWS_PAGE_SIZE
  )

  return (
    <section
      ref={sectionRef}
      className="bg-surface px-6 py-12 lg:px-panelx lg:py-panelx"
    >
      <div className="mx-auto flex max-w-wide flex-col items-center gap-14">
        <FilterPanel
          groups={groups}
          selected={{ categories: selected }}
          onToggle={handleToggle}
          keyword={keyword}
          onKeywordChange={setKeyword}
          onSubmit={handleSubmit}
        />

        {visibleNews.length > 0 ? (
          <ul className="grid w-full grid-cols-1 gap-x-10 gap-y-14 md:grid-cols-2 lg:grid-cols-3">
            {visibleNews.map((item) => (
              <CaseCard
                key={item.id}
                href={getNewsHref(item)}
                external={isFraimNews(item)}
                title={item.title}
                clampTitle
                tags={getNewsTags(item)}
                byline={{ date: getNewsDate(item) }}
                image={toCaseCardImage(item.eyecatch)}
              />
            ))}
          </ul>
        ) : (
          <p className="text-center text-base leading-body text-ink-subtle">
            条件に合うニュースが見つかりませんでした。
          </p>
        )}

        {isFiltered ? (
          <Pager
            current={page}
            totalPages={totalPages}
            onChange={handlePageChange}
          />
        ) : (
          <Pager
            current={page}
            totalPages={totalPages}
            hrefFor={getNewsListHref}
          />
        )}
      </div>
    </section>
  )
}
